import { Boxes, HeartPulse, LifeBuoy, PackageCheck, RotateCcw, Thermometer } from "lucide-react";
import type { ReactNode } from "react";

import type {
  RecipientHealthSummary,
  RecipientOrder,
  RecipientRmaSummary,
  RecipientSupportSummary,
  RecipientTelemetrySummary,
  Scope,
} from "@busright/data-contract";

import { ChartCard } from "@/components/ChartCard";
import { Drillable } from "@/components/Drillable";
import { EmptyState } from "@/components/EmptyState";
import { MetricLabel } from "@/components/MetricTooltip";
import { RiskTag } from "@/components/RiskTag";
import { formatInt, formatMb, formatScore } from "@/lib/format";

// =================================================================================================
// Reusable SCOPED PANELS — each takes a `Scope` (+ the slice of the RecipientScopedBundle it needs)
// and renders ONE field group. They compose into `ScopedEntityPage` (the District/Recipient page;
// later the Device page). Every metric carries a `MetricLabel` tooltip and, where a dataset exists,
// a `Drillable` wrapper. When a slice is null (endpoint not live yet) the panel shows a clearly
// marked empty/stub state — never a fabricated number.
//
// The bundle is fetched ONCE by the page and sliced into these panels, so a panel is a pure render
// of contract data. A panel that has no live endpoint yet (e.g. inventory detail) renders a labeled
// "pending live data" stub against the contract shape.
// =================================================================================================

// Shared "this panel's endpoint isn't live yet" stub — honest, never fabricated.
const PendingPanel = ({ note }: { readonly note: string }): ReactNode => (
  <EmptyState title="Pending live data" message={note} />
);

export interface HealthPanelProps {
  readonly scope: Scope;
  readonly health: RecipientHealthSummary | null;
}

// Device-health summary for the scope's fleet. Composite health is a SCORE (RAG eligible); the
// count drills into the device list behind it.
export const HealthPanel = ({ scope, health }: HealthPanelProps): ReactNode => (
  <ChartCard
    title="Health"
    subtitle="Device-health posture for this recipient's fleet"
    source="rpt_hw_device_latest"
  >
    {health ? (
      <div className="panel-stat-row">
        <Drillable metricKey="fleet.deviceCount" scope={scope}>
          <div className="panel-stat">
            <MetricLabel metricKey="fleet.deviceCount" label="Devices" />
            <span className="panel-stat-value br-tabular-nums">
              {formatInt(health.deviceCount)}
            </span>
          </div>
        </Drillable>
        <Drillable metricKey="health.composite" scope={scope}>
          <div className="panel-stat">
            <MetricLabel metricKey="health.composite" label="Avg composite" />
            <span className="panel-stat-value br-tabular-nums">
              {formatScore(health.avgCompositeHealthScore)}
            </span>
          </div>
        </Drillable>
        <div className="panel-risk-tags">
          <span className="panel-risk-cell">
            <RiskTag tier="OK" />
            <span className="br-tabular-nums">{formatInt(health.riskCounts.ok)}</span>
          </span>
          <span className="panel-risk-cell">
            <RiskTag tier="WARN" />
            <span className="br-tabular-nums">{formatInt(health.riskCounts.warn)}</span>
          </span>
          <span className="panel-risk-cell">
            <RiskTag tier="CRITICAL" />
            <span className="br-tabular-nums">{formatInt(health.riskCounts.critical)}</span>
          </span>
        </div>
      </div>
    ) : (
      <PendingPanel note="Device-health summary will populate once the recipient bundle is served." />
    )}
  </ChartCard>
);

export interface TelemetryPanelProps {
  readonly scope: Scope;
  readonly telemetry: RecipientTelemetrySummary | null;
}

// District-scoped telemetry — averages incl. the voltage/charging-underperformance signal. Memory
// is shown as a real MB fact (used/total), never a 0–100 score. Freshness is demoted to a subnote.
export const TelemetryPanel = ({ scope, telemetry }: TelemetryPanelProps): ReactNode => (
  <ChartCard
    title="Telemetry"
    subtitle="Latest device readings for this recipient — facts, not scores"
    source="esper_device_status (latest per device)"
  >
    {telemetry ? (
      <>
        <div className="panel-stat-row">
          <Drillable metricKey="battery.temperatureC" scope={scope}>
            <div className="panel-stat">
              <MetricLabel metricKey="battery.temperatureC" label="Avg battery temp" />
              <span className="panel-stat-value br-tabular-nums">
                {telemetry.avgBatteryTemperatureC === null
                  ? "—"
                  : `${telemetry.avgBatteryTemperatureC.toFixed(1)} °C`}
              </span>
            </div>
          </Drillable>
          <Drillable metricKey="battery.voltageV" scope={scope}>
            <div className="panel-stat">
              <MetricLabel metricKey="battery.voltageV" label="Avg voltage" />
              <span className="panel-stat-value br-tabular-nums">
                {telemetry.avgBatteryVoltageV === null
                  ? "—"
                  : `${telemetry.avgBatteryVoltageV.toFixed(2)} V`}
              </span>
            </div>
          </Drillable>
          <Drillable metricKey="memory.usedMb" scope={scope}>
            <div className="panel-stat">
              <MetricLabel metricKey="memory.usedMb" label="Avg memory used" />
              <span className="panel-stat-value br-tabular-nums">
                {telemetry.avgMemoryUsedMb === null && telemetry.avgMemoryTotalMb === null
                  ? "—"
                  : `${telemetry.avgMemoryUsedMb === null ? "—" : formatMb(telemetry.avgMemoryUsedMb)} / ${telemetry.avgMemoryTotalMb === null ? "—" : formatMb(telemetry.avgMemoryTotalMb)}`}
              </span>
            </div>
          </Drillable>
        </div>
        {telemetry.chargingUnderperforming ? (
          <p className="panel-flag">
            <Thermometer size={13} aria-hidden /> Charging voltage below the healthy band — worth a
            replacement investigation.
          </p>
        ) : null}
        <p className="panel-subnote">
          {telemetry.devicesReporting} devices reporting · {telemetry.stalePct.toFixed(0)}% of
          readings &gt; 2 days old
        </p>
      </>
    ) : (
      <PendingPanel note="Telemetry summary will populate once the recipient bundle is served." />
    )}
  </ChartCard>
);

export interface OrdersPanelProps {
  readonly scope: Scope;
  readonly orders: readonly RecipientOrder[];
}

// Orders placed to this recipient with parsed SKU lines + age/health. Empty (not stubbed) when the
// recipient genuinely has no orders.
export const OrdersPanel = ({ scope, orders }: OrdersPanelProps): ReactNode => (
  <ChartCard
    title="Orders"
    subtitle="What was shipped to this recipient, parsed SKU mix + age"
    source="stg_e2_orders (recipient = location_name)"
  >
    {orders.length === 0 ? (
      <EmptyState
        title="No orders"
        message="No supply-chain orders attributed to this recipient."
      />
    ) : (
      <ul className="panel-order-list">
        {orders.map((o, i) => (
          // Index disambiguates orders with both a null orderNbr and null orderDate (would collide).
          <li key={`${o.orderNbr ?? "?"}-${o.orderDate ?? "?"}-${i}`} className="panel-order">
            <div className="panel-order-head">
              <span className="panel-order-nbr br-tabular-nums">{o.orderNbr ?? "—"}</span>
              <span className="panel-order-date">{o.orderDate ?? "—"}</span>
              <Drillable
                metricKey="supply.orderQty"
                scope={scope}
                label="Drill into this order's quantity"
              >
                <span className="panel-order-qty br-tabular-nums">
                  {formatInt(o.totalQty)} units
                </span>
              </Drillable>
            </div>
            <div className="panel-order-skus">
              {o.skuLines.map((s) => (
                <span key={`${s.sku}-${s.description}`} className="panel-sku">
                  {formatInt(s.qty)}× {s.sku}
                  {s.inferred ? <span className="panel-inferred">inferred</span> : null}
                </span>
              ))}
            </div>
          </li>
        ))}
      </ul>
    )}
  </ChartCard>
);

export interface InventoryPanelProps {
  readonly scope: Scope;
}

// Inventory attributable to the recipient's orders. No live recipient-scoped inventory endpoint yet
// — render a labeled stub against the contract rather than fabricating quantities.
export const InventoryPanel = ({ scope }: InventoryPanelProps): ReactNode => (
  <ChartCard
    title="Inventory"
    subtitle="On-hand attributable to this recipient's fleet"
    source="supply.inventoryQtyAvailable"
  >
    <PendingPanel note="Recipient-scoped inventory is not yet served by an endpoint. Wiring is in place; values populate when /api/dataset serves supply.inventoryQtyAvailable at this scope." />
    <div className="panel-stub-cta">
      <Drillable metricKey="supply.inventoryQtyAvailable" scope={scope}>
        <span className="panel-stub-link">
          <Boxes size={13} aria-hidden /> Open inventory dataset
        </span>
      </Drillable>
    </div>
  </ChartCard>
);

export interface RmaPanelProps {
  readonly scope: Scope;
  readonly rmas: RecipientRmaSummary;
}

// RMA / returns summary for the recipient (open / aging / resolved). Counts drill into the RMA rows.
export const RmaPanel = ({ scope, rmas }: RmaPanelProps): ReactNode => (
  <ChartCard
    title="RMAs"
    subtitle="Returns + end-of-life signal for this recipient"
    source="stg_e2_rmas"
  >
    <div className="panel-stat-row">
      <Drillable metricKey="rma.openCount" scope={scope}>
        <div className="panel-stat">
          <MetricLabel metricKey="rma.openCount" label="Open" />
          <span className="panel-stat-value br-tabular-nums">{formatInt(rmas.openCount)}</span>
        </div>
      </Drillable>
      <div className="panel-stat">
        <MetricLabel metricKey="rma.agingDays" label="Aging" />
        <span className="panel-stat-value br-tabular-nums">{formatInt(rmas.agingCount)}</span>
      </div>
      <div className="panel-stat">
        <MetricLabel metricKey="rma.resolvedCount" label="Resolved" />
        <span className="panel-stat-value br-tabular-nums">{formatInt(rmas.resolvedCount)}</span>
      </div>
    </div>
  </ChartCard>
);

export interface SupportPanelProps {
  readonly support: RecipientSupportSummary;
}

// Support / Linear ticket summary for the recipient (counts by workflow state). Read-only summary —
// no drill (tickets live in Linear, not the dataset endpoint).
export const SupportPanel = ({ support }: SupportPanelProps): ReactNode => {
  const states = Object.entries(support.byState);
  return (
    <ChartCard
      title="Support"
      subtitle="Linear tickets for this recipient, by workflow state"
      source="Linear (source of truth)"
    >
      {support.total === 0 ? (
        <EmptyState title="No tickets" message="No Linear tickets linked to this recipient." />
      ) : (
        <div className="panel-support">
          <div className="panel-stat">
            <span className="panel-stat-label">
              <LifeBuoy size={13} aria-hidden /> Total
            </span>
            <span className="panel-stat-value br-tabular-nums">{formatInt(support.total)}</span>
          </div>
          <ul className="panel-support-states">
            {states.map(([state, count]) => (
              <li key={state}>
                <span className="panel-support-state">{state}</span>
                <span className="br-tabular-nums">{formatInt(count)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </ChartCard>
  );
};

// Icons re-exported for the page header composing these panels.
export const PANEL_ICONS = {
  health: HeartPulse,
  orders: PackageCheck,
  rma: RotateCcw,
} as const;
